import { useState } from "react";
import {
  Box,
  Button,
  Input,
  ScrollArea,
  useIsMobile,
} from "@salestrak/ui";
import { PageHeader, PageHeaderBack, PageHeaderSubtitle, PageHeaderTitle } from "#app/components/page-header";
import { SettingsGroup, SettingsGroupContent, SettingsGroupRow } from "./settings-group";
import { PermissionEdit } from "./permission-edit";
import { FULL_PERMISSIONS, type Permissions } from "./permission-badge";
import type { Omittable } from "@salestrak/ui";

type ApiKeyFormViewProps = Omittable<{
  apiKey?: {
    apiKeyId: string;
    name: string;
    permissions: Permissions;
  } | null;
  onCancel: () => void;
  onSubmit: (data: { name: string; permissions: Permissions }) => Promise<void>;
}>;

export function ApiKeyFormView({
  apiKey,
  onCancel,
  onSubmit,
}: ApiKeyFormViewProps) {
  const isEdit = !!apiKey;
  const isMobile = useIsMobile();
  const [name, setName] = useState(apiKey?.name ?? "");
  const [permissions, setPermissions] = useState<Permissions>(
    apiKey?.permissions ?? FULL_PERMISSIONS,
  );
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit({ name: name.trim(), permissions });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderBack label="API" onClick={onCancel} />
          <PageHeaderTitle>{isEdit ? "Edit API key" : "Create API key"}</PageHeaderTitle>
          <PageHeaderSubtitle>
            All actions performed using the API key are attributed to you personally.
          </PageHeaderSubtitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box
            as="form"
            gap="xl"
            fill="width"
            maxWidth={800}
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
          >
            <SettingsGroup label="Settings">
              <SettingsGroupRow label="Key name" subtitle="Descriptive name for this API key">
                <Input
                  placeholder="Enter name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus={!isMobile}
                />
              </SettingsGroupRow>

              <SettingsGroupContent>
                <PermissionEdit value={permissions} onChange={setPermissions} />
              </SettingsGroupContent>
            </SettingsGroup>

            <Box direction="row" gap="sm" justify="end">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit" disabled={!name.trim() || submitting}>
                {isEdit ? "Save changes" : "Create key"}
              </Button>
            </Box>
          </Box>
        </Box>
      </ScrollArea>
    </Box>
  );
}
